import { type RefObject, useEffect } from "react";
import { type ContextMenuItem, initialFocusIndex } from "./items";

/**
 * What a panel does with focus when it appears: the root and keyboard-opened submenus focus
 * their first enabled item; submenus opened by hover leave focus on the parent item.
 */
export type InitialFocus = "first-item" | "keep";

export interface InitialFocusTarget {
  items: ContextMenuItem[];
  panelRef: RefObject<HTMLElement | null>;
  element: (index: number) => HTMLElement | null;
}

/** Applies `initialFocus` once the panel has been placed (`placed` turns true). */
export function useInitialFocus(placed: boolean, initialFocus: InitialFocus, target: InitialFocusTarget): void {
  useEffect(() => {
    if (!placed || initialFocus !== "first-item") return;
    const first = initialFocusIndex(target.items);
    const el = first !== undefined ? target.element(first) : null;
    (el ?? target.panelRef.current)?.focus({ preventScroll: true });
    // Only when the panel appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed]);
}
